import { ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';

/**
 * Stoppable wrapper around VueUse's useDebounceFn.
 *
 * Example usage:
 * const { debounced, stop, isStopped } = useDebounceFunctionStoppable(fn, 200)
 * debounced(arg1, arg2)
 * stop() // prevent the next scheduled call from executing fn
 *
 * Notes:
 * - Calling `debounced(...)` resets the stopped flag to false.
 * - `stop()` affects only the next scheduled invocation. If you call `debounced` again later,
 *   it will allow execution unless you call `stop()` again.
 * - The returned object forwards `cancel` and `flush` to the inner debounced function when available.
 */
export default function useDebounceFunctionStoppable(
  function_,
  ms = 200,
  options = {}
) {
  const stopping = ref(false);

  const inner = useDebounceFn(
    (...arguments_) => {
      if (!stopping.value) {
        return function_.apply(null, arguments_);
      }
      // if stopped, swallow the call; keep the stopped state for a single run
    },
    ms,
    options
  );

  const debounced = (...arguments_) => {
    // Reset the stopped flag for a new cycle
    stopping.value = false;
    return inner.call(null, ...arguments_);
  };

  const stop = () => {
    stopping.value = true;
  };

  // Forward controls if present
  debounced.cancel = inner?.cancel?.bind?.(inner);
  debounced.flush = inner?.flush?.bind?.(inner);

  return { debounced, stop, isStopped: stopping };
}
